import { Card, Avatar } from 'antd'
import { TrophyOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import useGetAnalyticsData from '@forwardflow/hooks/customHooks/marketing/useGetAnalyticsData'

const OutreachPerformance = () => {
   const { data: analyticsData } = useGetAnalyticsData()

   const getInitials = (fullName: string) => {
      if (!fullName) return 'N/A'
      return fullName
         .split(' ')
         .map((name) => name.charAt(0))
         .join('')
         .toUpperCase()
   }

   const getBestPerformer = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null

      const validAgents = agentStats.filter(agent =>
         agent.conversationsPerAppointment &&
         agent.conversationsPerAppointment > 0
      )

      if (validAgents.length === 0) return null

      return validAgents.reduce((prev, current) =>
         prev.conversationsPerAppointment < current.conversationsPerAppointment ? prev : current
      )
   }

   const getBestTextPerformer = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null

      const validAgents = agentStats.filter(agent =>
         agent.textResponseRate &&
         agent.textResponseRate > 0
      )

      if (validAgents.length === 0) return null

      return validAgents.reduce((prev, current) =>
         prev.textResponseRate > current.textResponseRate ? prev : current
      )
   }

   const getWorstTextPerformer = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null

      const validAgents = agentStats.filter(agent =>
         agent.textResponseRate &&
         agent.textResponseRate > 0
      )

      if (validAgents.length === 0) return null

      return validAgents.reduce((prev, current) =>
         prev.textResponseRate < current.textResponseRate ? prev : current
      )
   }

   const getWorstPerformer = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null

      const validAgents = agentStats.filter(agent =>
         agent.conversationsPerAppointment &&
         agent.conversationsPerAppointment > 0
      )

      if (validAgents.length === 0) return null

      return validAgents.reduce((prev, current) =>
         prev.conversationsPerAppointment > current.conversationsPerAppointment ? prev : current
      )
   }

   const getBestConversationalist = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null

      const validAgents = agentStats.filter(agent =>
         agent.callAttemptsPerConversation &&
         agent.callAttemptsPerConversation > 0
      )

      if (validAgents.length === 0) return null

      return validAgents.reduce((prev, current) =>
         prev.callAttemptsPerConversation < current.callAttemptsPerConversation ? prev : current
      )
   }

   const getWorstConversationalist = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null

      const validAgents = agentStats.filter(agent =>
         agent.callAttemptsPerConversation &&
         agent.callAttemptsPerConversation > 0
      )

      if (validAgents.length === 0) return null

      return validAgents.reduce((prev, current) =>
         prev.callAttemptsPerConversation > current.callAttemptsPerConversation ? prev : current
      )
   }

   const bestPerformer = getBestPerformer(analyticsData?.agentStats)
   const worstPerformer = getWorstPerformer(analyticsData?.agentStats)
   const bestConversationalist = getBestConversationalist(analyticsData?.agentStats)
   const worstConversationalist = getWorstConversationalist(analyticsData?.agentStats)
   const bestTextRsponsePerformer = getBestTextPerformer(analyticsData?.agentStats)
   const worstTextRsponsePerformer = getWorstTextPerformer(analyticsData?.agentStats)

   // Calculate grid columns dynamically based on available cards
   const appointmentCards = [bestPerformer, worstPerformer, bestConversationalist, worstConversationalist].filter(Boolean)
   const textCards = [bestTextRsponsePerformer, worstTextRsponsePerformer].filter(Boolean)

   // Check if we have data for each section
   const hasAppointmentData = analyticsData?.avgConversationsPerAppointment || 
                              analyticsData?.avgCallAttemptsPerConversation || 
                              analyticsData?.avgCallAttemptsPerLead
   const hasTextData = analyticsData?.peopleTexted && analyticsData?.peopleResponded

   const getGridClass = (cardCount: number) => {
      if (cardCount === 0) return 'hidden'
      if (cardCount === 1) return 'grid grid-cols-1 gap-6 mb-8 lg:grid-cols-1'
      if (cardCount === 2) return 'grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2'
      if (cardCount === 3) return 'grid grid-cols-1 gap-6 mb-8 lg:grid-cols-3'
      return 'grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2'
   }

   return (
      <div className="p-6 shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 rounded-xl overflow-hidden backdrop-blur-sm">
         <div className="flex flex-col gap-6 lg:flex-row">
            {/* Left Sidebar */}
            <div className="flex-shrink-0 lg:w-80">
               <h2 className="mb-6 text-2xl font-bold text-white">
                  Outreach & Appointment Performance
               </h2>

               {/* Pro Tip Card */}
               <Card
                  className="border-yellow-600"
                  style={{
                     backgroundColor: '#1f2937',
                     borderColor: '#ca8a04',
                     color: '#e5e7eb',
                  }}
               >
                  <div className="flex items-start gap-3">
                     <div className="flex-shrink-0 px-3 py-1 text-black bg-yellow-500 rounded-full">
                        <span className="text-sm font-bold">Pro tip</span>
                     </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-gray-300">
                     Appointment performance is one of the key leading indicators of success for real estate teams. Make sure you have set outreach expectations and tracking in place!
                  </p>
               </Card>
            </div>

            {/* Main Content */}
            <div className="flex-1">
               {/* If no data at all, show no data message */}
               {!hasAppointmentData && !hasTextData && appointmentCards.length === 0 && textCards.length === 0 ? (
                  <div className="py-12 text-center">
                     <div className="mb-6">
                        <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 bg-gray-600 rounded-full opacity-50">
                           <span className="text-3xl">📞</span>
                        </div>
                     </div>
                     <h3 className="mb-3 text-xl font-semibold text-white">No Outreach Data Available</h3>
                     <p className="text-gray-400 max-w-md mx-auto">
                        Your outreach and appointment performance metrics will appear here once you start making calls, sending texts, and setting appointments.
                     </p>
                  </div>
               ) : (
                  <>
                     {/* Appointment Section - Only show if we have appointment data */}
                     {hasAppointmentData && (
                        <div>
                           <div className="mb-8">
                              <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
                                 <h3 className="text-xl font-semibold text-white">
                                    How many conversations does it take to get an
                                    appointment?
                                 </h3>
                              </div>

                              {analyticsData?.avgConversationsPerAppointment && (
                                 <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-start">
                                    <div className="flex items-center justify-center flex-shrink-0 p-3 bg-red-900 rounded-full">
                                       <ExclamationCircleOutlined className="text-xl text-red-400" />
                                    </div>
                                    <div>
                                       <p className="text-gray-300">
                                          This month, your team averaged{' '}
                                          <span className="font-semibold text-white">
                                             {analyticsData.avgConversationsPerAppointment} conversations
                                          </span>{' '}
                                          to get an appointment.
                                       </p>
                                    </div>
                                 </div>
                              )}

                              {/* Stats Card - Only show if we have the required data */}
                              {(analyticsData?.avgCallAttemptsPerConversation || analyticsData?.avgCallAttemptsPerLead) && (
                                 <Card
                                    style={{
                                       backgroundColor: '#1f2937',
                                       borderColor: '#374151',
                                       color: '#e5e7eb',
                                    }}
                                 >
                                    <p className="text-gray-300">
                                       {analyticsData?.avgCallAttemptsPerConversation && (
                                          <>
                                             It took your team{' '}
                                             <span className="font-semibold text-white">
                                                {analyticsData.avgCallAttemptsPerConversation} phone
                                                call attempts
                                             </span>{' '}
                                             on average to have{' '}
                                             <span className="font-semibold text-white">
                                                1 conversation
                                             </span>
                                             {analyticsData?.avgCallAttemptsPerLead && '. '}
                                          </>
                                       )}
                                       {analyticsData?.avgCallAttemptsPerLead && (
                                          <>
                                             Your team on average made{' '}
                                             <span className="font-semibold text-white">
                                                {analyticsData.avgCallAttemptsPerLead} call attempts
                                                per lead
                                             </span>
                                             .
                                          </>
                                       )}
                                    </p>
                                 </Card>
                              )}
                           </div>

                           {/* Performance Cards Grid - Only show if there are valid cards */}
                           {appointmentCards.length > 0 && (
                              <div className={getGridClass(appointmentCards.length)}>
                                 {/* Top Appointment Closer */}
                                 {bestPerformer && (
                                    <Card
                                       style={{
                                          backgroundColor: '#1f2937',
                                          borderColor: '#374151',
                                          color: '#e5e7eb',
                                       }}
                                    >
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="flex-1 min-w-0">
                                             <h4 className="font-semibold text-white truncate">
                                                Top Appointment Closer
                                             </h4>
                                             <p className="text-gray-400 truncate">
                                                {bestPerformer.agentName}
                                             </p>
                                          </div>
                                          <div className="flex items-center gap-2 ml-3 shrink-0">
                                             <div className="flex items-center justify-center w-8 h-8 bg-green-900 rounded-full shrink-0">
                                                <TrophyOutlined className="text-green-400" />
                                             </div>

                                             <Avatar className="text-white bg-gray-600">
                                                {getInitials(bestPerformer.agentName)}
                                             </Avatar>
                                          </div>
                                       </div>
                                       <p className="font-medium text-green-400">
                                          {bestPerformer.conversationsPerAppointment}{' '}
                                          conversations per appointment
                                       </p>
                                    </Card>
                                 )}

                                 {/* Struggling to Close Appointments */}
                                 {worstPerformer && (
                                    <Card
                                       style={{
                                          backgroundColor: '#1f2937',
                                          borderColor: '#374151',
                                          color: '#e5e7eb',
                                       }}
                                    >
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="flex-1 min-w-0">
                                             <h4 className="font-semibold text-white truncate">
                                                Struggling to Close Appointments
                                             </h4>
                                             <p className="text-gray-400 truncate">
                                                {worstPerformer.agentName}
                                             </p>
                                          </div>
                                          <div className="flex items-center gap-2 ml-3">
                                             <div className="flex items-center justify-center w-8 h-8 p-1 bg-red-900 rounded-full">
                                                <ExclamationCircleOutlined className="text-red-400" />
                                             </div>

                                             <Avatar className="text-white bg-gray-600">
                                                {getInitials(worstPerformer.agentName)}
                                             </Avatar>
                                          </div>
                                       </div>
                                       <p className="font-medium text-red-400">
                                          {worstPerformer.conversationsPerAppointment}{' '}
                                          conversations per appointment
                                       </p>
                                    </Card>
                                 )}

                                 {/* Most Effective Conversationalist */}
                                 {bestConversationalist && (
                                    <Card
                                       style={{
                                          backgroundColor: '#1f2937',
                                          borderColor: '#374151',
                                          color: '#e5e7eb',
                                       }}
                                    >
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="flex-1 min-w-0">
                                             <h4 className="font-semibold text-white truncate">
                                                Most Effective Conversationalist
                                             </h4>
                                             <p className="text-gray-400 truncate">
                                                {bestConversationalist.agentName}
                                             </p>
                                          </div>
                                          <div className="flex items-center gap-2 ml-3">
                                             <div className="flex items-center justify-center w-8 h-8 p-1 bg-green-900 rounded-full">
                                                <TrophyOutlined className="text-green-400" />
                                             </div>

                                             <Avatar className="text-white bg-gray-600">
                                                {getInitials(bestConversationalist.agentName)}
                                             </Avatar>
                                          </div>
                                       </div>
                                       <p className="font-medium text-green-400">
                                          {bestConversationalist.callAttemptsPerConversation}{' '}
                                          call attempts per conversation
                                       </p>
                                    </Card>
                                 )}

                                 {/* Struggling to Connect */}
                                 {worstConversationalist && (
                                    <Card
                                       style={{
                                          backgroundColor: '#1f2937',
                                          borderColor: '#374151',
                                          color: '#e5e7eb',
                                       }}
                                    >
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="flex-1 min-w-0">
                                             <h4 className="font-semibold text-white truncate">
                                                Struggling to Connect
                                             </h4>
                                             <p className="text-gray-400 truncate">
                                                {worstConversationalist.agentName}
                                             </p>
                                          </div>
                                          <div className="flex items-center gap-2 ml-3">
                                             <div className="flex items-center justify-center w-8 h-8 p-1 bg-red-900 rounded-full">
                                                <ExclamationCircleOutlined className="text-red-400" />
                                             </div>
                                             <Avatar className="text-white bg-gray-600">
                                                {getInitials(worstConversationalist.agentName)}
                                             </Avatar>
                                          </div>
                                       </div>
                                       <p className="font-medium text-red-400">
                                          {worstConversationalist.callAttemptsPerConversation}{' '}
                                          call attempts per conversation
                                       </p>
                                    </Card>
                                 )}
                              </div>
                           )}
                        </div>
                     )}

                     {/* Text Response Section - Only show if we have text data */}
                     {hasTextData && (
                        <div>
                           <div className="mb-8">
                              <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
                                 <h3 className="text-xl font-semibold text-white">
                                    Are your outbound texts getting responses from clients?
                                 </h3>
                              </div>

                              <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-start">
                                 <div className="flex items-center justify-center flex-shrink-0 p-3 bg-red-900 rounded-full">
                                    <ExclamationCircleOutlined className="text-xl text-red-400" />
                                 </div>
                                 <div>
                                    <p className="text-gray-300">
                                       This month, your team texted{' '}
                                       <span className="font-semibold text-white">
                                          {analyticsData.peopleTexted} people
                                       </span>{' '} and {' '}
                                       <span className="font-semibold text-white">
                                          {analyticsData.peopleResponded} people
                                       </span>{' '} responded at least once, which is {' '}
                                       <span className="font-semibold text-white">
                                          {((analyticsData.peopleResponded / analyticsData.peopleTexted) * 100).toFixed(1)}%
                                       </span>{' '}
                                       text response rate.
                                    </p>
                                 </div>
                              </div>
                           </div>

                           {/* Text Performance Cards Grid - Only show if there are valid cards */}
                           {textCards.length > 0 && (
                              <div className={getGridClass(textCards.length)}>
                                 {/* Best Text Response Rate */}
                                 {bestTextRsponsePerformer && (
                                    <Card
                                       style={{
                                          backgroundColor: '#1f2937',
                                          borderColor: '#374151',
                                          color: '#e5e7eb',
                                       }}
                                    >
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="flex-1 min-w-0">
                                             <h4 className="font-semibold text-white truncate">
                                                Best Text Response Rate
                                             </h4>
                                             <p className="text-gray-400 truncate">
                                                {bestTextRsponsePerformer.agentName}
                                             </p>
                                          </div>
                                          <div className="flex items-center gap-2 ml-3 shrink-0">
                                             <div className="flex items-center justify-center w-8 h-8 bg-green-900 rounded-full shrink-0">
                                                <TrophyOutlined className="text-green-400" />
                                             </div>

                                             <Avatar className="text-white bg-gray-600">
                                                {getInitials(bestTextRsponsePerformer.agentName)}
                                             </Avatar>
                                          </div>
                                       </div>
                                       <p className="font-medium text-green-400">
                                          Leads respond{' '}
                                          {(bestTextRsponsePerformer.textResponseRate * 100).toFixed(1)}%{' '}
                                          of the time
                                       </p>
                                    </Card>
                                 )}

                                 {/* Worst Text Response Rate */}
                                 {worstTextRsponsePerformer && (
                                    <Card
                                       style={{
                                          backgroundColor: '#1f2937',
                                          borderColor: '#374151',
                                          color: '#e5e7eb',
                                       }}
                                    >
                                       <div className="flex items-center justify-between mb-3">
                                          <div className="flex-1 min-w-0">
                                             <h4 className="font-semibold text-white truncate">
                                                Worst Text Response Rate
                                             </h4>
                                             <p className="text-gray-400 truncate">
                                                {worstTextRsponsePerformer.agentName}
                                             </p>
                                          </div>
                                          <div className="flex items-center gap-2 ml-3">
                                             <div className="flex items-center justify-center w-8 h-8 p-1 bg-red-900 rounded-full">
                                                <ExclamationCircleOutlined className="text-red-400" />
                                             </div>

                                             <Avatar className="text-white bg-gray-600">
                                                {getInitials(worstTextRsponsePerformer.agentName)}
                                             </Avatar>
                                          </div>
                                       </div>
                                       <p className="font-medium text-red-400">
                                          Leads respond{' '}
                                          {(worstTextRsponsePerformer.textResponseRate * 100).toFixed(1)}%{' '}
                                          of the time
                                       </p>
                                    </Card>
                                 )}
                              </div>
                           )}
                        </div>
                     )}
                  </>
               )}
            </div>
         </div>
      </div>
   )
}

export default OutreachPerformance